import { useEffect, useState } from 'react';

export const THEMES = ['obsidian', 'light', 'dark'] as const;
export type Theme = (typeof THEMES)[number];

const STORAGE_KEY = 'cq.theme';

export const THEME_LABELS: Record<Theme, string> = {
  obsidian: 'Obsidian',
  light: 'Light',
  dark: 'Dark',
};

function isTheme(value: unknown): value is Theme {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value);
}

/**
 * Obsidian is the default for everyone, on purpose: it is the site's identity,
 * and a visitor who has never chosen should see it. The stored preference wins
 * once they have.
 *
 * localStorage can throw outright in a private window or with site data
 * blocked, so every access is guarded.
 */
export function readTheme(fallback: Theme = 'obsidian'): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isTheme(stored)) return stored;
  } catch {
    // Storage unavailable; the default is a perfectly good answer.
  }
  return fallback;
}

export function applyTheme(theme: Theme): void {
  document.documentElement.dataset['theme'] = theme;
}

export function useTheme(fallback: Theme = 'obsidian'): [Theme, (next: Theme) => void] {
  const [theme, setThemeState] = useState<Theme>(() => readTheme(fallback));

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = (next: Theme): void => {
    setThemeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // The choice still applies for this page view.
    }
  };

  return [theme, setTheme];
}

/**
 * Drives the specular sweep from pointer position.
 *
 * Attached only where the gloss is visible; the light and dark themes zero out
 * --gloss-enabled, so the listener still runs but paints nothing.
 */
export function trackGloss(element: HTMLElement, event: { clientX: number; clientY: number }): void {
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return;

  element.style.setProperty('--gloss-x', `${((event.clientX - rect.left) / rect.width) * 100}%`);
  element.style.setProperty('--gloss-y', `${((event.clientY - rect.top) / rect.height) * 100}%`);
}
